import type { ConditionalActionValueType } from '@web-config-app/core';
import type { ConditionalActionOperator } from './types';

/**
 * The conditional-action condition operators. Each entry is plain metadata —
 * a locale-key label, the logical value types it applies to, whether it
 * requires the active source to be catalog-backed, and the default input
 * values when the operator is first selected. No React in this module.
 *
 * The v1 catalog is a single set-membership pair — `includes` ("is any of")
 * and `not_includes` ("is none of"). Both take a set of comparison codes and
 * test set overlap, so one pair covers "is", "is one of", and multi-select
 * (`allowMultiple`) answers uniformly: the runtime normalizes the answer
 * (a single code or an array of codes) to a set and checks intersection.
 *
 * This covers ~92.5% of real production branching rules — see
 * `docs/branching-rule-types-survey.md` (349 rules across emblem-health,
 * medibank, quest). Numeric comparison operators (`>`, `>=`, `<`, `<=`) extend
 * the catalog for numeric subjects (member data points such as `up.age`): they
 * take a single scalar value rather than a set, so each operator carries an
 * `inputShape` and `operator-inputs` branches on it.
 */
export const CONDITIONAL_ACTION_OPERATORS: ConditionalActionOperator[] = [
  {
    id: 'includes',
    label: 'CONDITIONAL_ACTION_INCLUDES_LABEL',
    types: ['string', 'number', 'date', 'datetime', 'time'],
    requiresValueCatalog: true,
    inputShape: 'set',
    defaultInputs: () => ({ values: [] }),
  },
  {
    id: 'not_includes',
    label: 'CONDITIONAL_ACTION_NOT_INCLUDES_LABEL',
    types: ['string', 'number', 'date', 'datetime', 'time'],
    requiresValueCatalog: true,
    inputShape: 'set',
    defaultInputs: () => ({ values: [] }),
  },
  {
    id: '>',
    label: 'CONDITIONAL_ACTION_GREATER_THAN_LABEL',
    types: ['number'],
    requiresValueCatalog: false,
    inputShape: 'scalar',
    defaultInputs: () => ({ value: '' }),
  },
  {
    id: '>=',
    label: 'CONDITIONAL_ACTION_GREATER_THAN_OR_EQUAL_LABEL',
    types: ['number'],
    requiresValueCatalog: false,
    inputShape: 'scalar',
    defaultInputs: () => ({ value: '' }),
  },
  {
    id: '<',
    label: 'CONDITIONAL_ACTION_LESS_THAN_LABEL',
    types: ['number'],
    requiresValueCatalog: false,
    inputShape: 'scalar',
    defaultInputs: () => ({ value: '' }),
  },
  {
    id: '<=',
    label: 'CONDITIONAL_ACTION_LESS_THAN_OR_EQUAL_LABEL',
    types: ['number'],
    requiresValueCatalog: false,
    inputShape: 'scalar',
    defaultInputs: () => ({ value: '' }),
  },
];

/**
 * The operators offered for a subject. `hasValueOptions` is whether an
 * enumerated set of selectable values actually resolves for the item (today,
 * a choice question's answer options) — not merely whether the source declares
 * a `valueCatalog`. A quantity question and a data point both resolve to
 * `number`, so `subjectType` breaks the tie: numeric (scalar) operators are
 * data-point-only for now — question numerics are deferred.
 */
export const applicableOperators = (
  valueType: ConditionalActionValueType | undefined,
  hasValueOptions: boolean,
  subjectType: string | undefined,
): ConditionalActionOperator[] => {
  if (!valueType) return [];
  return CONDITIONAL_ACTION_OPERATORS.filter((op) => {
    if (!op.types.includes(valueType)) return false;
    if (op.requiresValueCatalog && !hasValueOptions) return false;
    if (op.inputShape === 'scalar' && subjectType !== 'dataPoint') return false;
    return true;
  });
};
